import React, { useContext, useEffect, useRef, useState } from "react";
import { OidcProviderButton } from "./OidcProviderButton";
import useSearchParams from "@src/utils/useSearchParams";
import { logEvent } from "@src/utils/logClient";
import ErrorBox from "../mobile/ErrorBox";
import { TranslationContext } from "../translation/TranslationContext";
import axios from "@src/utils/requests/axiosInstance";

interface OidcContinueButtonProps {
  providerSlug: string;
  accountType?: string;
}

// Normalize account_type for the web.external_page.oidc.click event ONLY, so the SSO-click
// funnel joins cleanly to the downstream completion events (web.signup.success anchors the leader
// population on "school_leader"; LoginForm passes "leader" for the same population). Scoped to this
// event — callers keep passing their native values for their own events. Undefined -> "unknown" so
// the click still lands in an explicit bucket rather than dropping out of grouped funnels.
const normalizeAccountTypeForClickEvent = (accountType?: string): string => {
  if (!accountType) {
    return "unknown";
  }
  if (accountType === "leader") {
    return "school_leader";
  }
  return accountType;
};

export const OidcContinueButton: React.FC<OidcContinueButtonProps> = ({ providerSlug, accountType }) => {
  const t = useContext(TranslationContext);
  const [showOidcError, setShowOidcError] = useState(false);
  const desiredEmail = useSearchParams("email")[0];
  // Synchronous in-flight guard: one click = one verification_url request (and one click event).
  // useRef (not useState) so the second of two fast clicks sees the flag before any re-render —
  // a useState flag would be read stale because setState is async.
  const inFlightRef = useRef(false);

  // The happy path intentionally leaves inFlightRef set so the button can't re-fire in the gap
  // before the cross-origin redirect unloads the page (see handleClick). The one case where the
  // page is NOT unloaded is a back-forward-cache restore (browser Back, reproducible in Safari):
  // the frozen page is resurrected with the guard still set, leaving the button dead. Reset it on
  // pageshow when persisted=true (the bfcache-restore signal). Gated on persisted only — not
  // focus/visibilitychange, which fire on tab switches and would wrongly unlock an in-flight request.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        // Only release the in-flight guard. Don't touch showOidcError: an error is only ever shown
        // on a path that already released the guard (so it isn't the latched-guard case this
        // handler exists for), and clearing it here would silently wipe an error the user may not
        // have read yet if they navigate forward and back.
        inFlightRef.current = false;
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  const handleClick = () => {
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;

    // Fire the click event synchronously, before the verification_url request and redirect, so
    // it captures the SSO-click intent (the top-of-funnel denominator) even if those steps fail.
    logEvent({
      eventName: "web.external_page.oidc.click",
      metadata: {
        provider: providerSlug,
        current_site: "external",
        account_type: normalizeAccountTypeForClickEvent(accountType),
      },
    });

    const baseUrl = process.env.GATSBY_TEACHER_LOGIN_URL || "https://teach.classdojo.com";
    const redirectTo = `${baseUrl}/#/oidc/callback`;
    const url = `${baseUrl}/api/oidc/mobile/${providerSlug}/verification_url`;

    axios
      .get(url, {
        params: {
          redirectTo,
          email: desiredEmail,
          loginHint: desiredEmail,
          prompt: providerSlug === "google" ? "select_account consent" : undefined,
          bypassUniversalLink: "true",
        },
      })
      .then((response) => {
        if (response.data?.url) {
          // Navigating away — intentionally leave the guard set so it can't fire again before the
          // redirect unloads the page. The pageshow handler above clears it on bfcache restore, the
          // one case where the page comes back without unloading.
          window.location.href = response.data.url;
          return;
        }
        // 200 without a usable URL: surface the error and release the guard so the user can retry,
        // rather than leaving the button silently dead.
        logEvent({
          eventName: "web.external_page.oidc.error",
          metadata: {
            provider: providerSlug,
            error: "missing verification_url",
          },
        });
        setShowOidcError(true);
        inFlightRef.current = false;
      })
      .catch((error) => {
        logEvent({
          eventName: "web.external_page.oidc.error",
          metadata: {
            provider: providerSlug,
            error: error.message,
          },
        });
        setShowOidcError(true);
        // Allow a genuine retry after a failure.
        inFlightRef.current = false;
      });
  };

  return (
    <>
      <button onClick={handleClick} style={{ width: "100%" }}>
        <OidcProviderButton provider={providerSlug} />
      </button>
      {showOidcError && <ErrorBox>{t.translate("components.signup_teacher.oidc_error")}</ErrorBox>}
    </>
  );
};
